import { Button, Segmented } from 'antd';
import { Plus } from 'lucide-react';
import { useNavigate, useParams } from 'react-router';
import { FLAGGED_AREAS_PATH } from '../features/flagged-areas/flagged-areas.paths';
import { PageHeader } from '../../../shared/components/PageHeader';
import { sectionTrail } from '../../../shared/components/section-trail';
import { useZoneNavigation } from '../hooks/use-zone-navigation';
import { ZONE_VIEW_LABELS, ZONE_VIEWS, type ZoneView } from '../zones.paths';
import { ZonesMapView } from '../features/map/components/ZonesMapView';
import { ZonesTableView } from '../features/list/components/ZonesTableView';

interface ZonesPageProps {
  view: ZoneView;
  /** Set on a zone's routes: the map view with its panel open (`/zones/:id`), or the table with its sheet open (`/zones/list/:id`). */
  panel?: 'new' | 'zone';
}

function useOpenZoneId(panel: ZonesPageProps['panel']): string | undefined {
  const { id } = useParams();
  if (panel === 'new') return 'new';
  return panel === 'zone' ? id : undefined;
}

/**
 * The Zones section: every zone on a map coloured by kind, or as a table, switched by the header's
 * Map | Table control. Opening a zone, from either, shows its panel beside the map. One component for
 * every zones route, so the map stays mounted as panels open and close.
 */
export function ZonesPage({ view, panel }: ZonesPageProps) {
  const openZoneId = useOpenZoneId(panel);
  const navigation = useZoneNavigation(view);
  const navigate = useNavigate();

  return (
    <>
      <PageHeader
        breadcrumbs={sectionTrail('zones')}
        title="Zones and boundaries"
        description="Places are where jobs go: ports, depots, borders and customer sites. Boundaries are gate lanes, weighbridges and border posts, always watched for arrivals and departures and never offered as a destination."
        actions={
          <>
            <Button onClick={() => navigate(FLAGGED_AREAS_PATH)}>Flagged areas</Button>
            <Segmented<ZoneView>
              aria-label="View"
              value={view}
              onChange={navigation.switchView}
              options={ZONE_VIEWS.map((value) => ({ value, label: ZONE_VIEW_LABELS[value] }))}
            />
            <Button type="primary" icon={<Plus className="size-4" />} onClick={navigation.openNewZone}>
              New zone
            </Button>
          </>
        }
      />
      {view === 'list' ? <ZonesTableView openZoneId={openZoneId} /> : <ZonesMapView openZoneId={openZoneId} />}
    </>
  );
}
